import React, { useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { API, CONTACT_MAX_NOTES_LEN, genderOptions } from '../../../shared/constants/constants';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { openSnackbar } from '../../AppSnackbar/snackbarSlice';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import MergeIcon from '@mui/icons-material/Merge';
import ArchiveIcon from '@mui/icons-material/Archive';
import MergeContactPersonnelModal from './MergeContactPersonnelModal';
import AlertChangeOrgUnit from '../AlertChangeOrgUnit';
import { normalizeCommunityMemberPayload } from '../../../shared/utils/communityMemberNormalization';
import {
  getCommunityMembersByCaseInsensitiveNameAndOrganization,
  getExpertsByCaseInsensitiveNameAndUnit,
  getJournalistsByCaseInsensitiveNameAndOrg,
} from '../contactsRequest';
import { useArchiveContact } from '../Hooks/useArchiveContact';
import { ComboBoxCreatableOutlet } from '../../Cells/CellTypes';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { normalizeExpertPayload } from '../../../utils/expertNormalization';
import { normalizeJournalistPayload } from '../../../shared/functions/normalizeJournalistPayload';

const useStyles = makeStyles(({ palette, tabColor }) => ({
  cellInput: {
    display: 'flex',
    flexDirection: 'column',
  },
  smCellInput: {
    width: 80,
  },
  smCellInputContainer: {
    maxWidth: 80,
  },
  groupLabel: {
    background: ({ tabColor }) => `${tabColor} !important`,
    color: `${palette.primary.contrastText} !important`,
    fontWeight: '700 !important',
  },
  dialog: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '25rem',
    width: '30rem',
    overflow: 'hidden',
  },
  charCountText: {
    color: 'rgba(0, 0, 0, 0.6)',
  },
}));

const CombinedContactPersonnelModal = (props) => {
  // Props for adding a new contact
  const {
    openAddContact,
    setOpenAddContact,
    orgs,
    setRefreshContacts,
    setRefreshOrgs,
    category,
    units,
    customFieldConfigs,
  } = props;
  // Props for editing a contact on Contacts page
  const { isEditing, selectedContact } = props;
  // Props for entering from MediaInteractions/ServicePage/EngagementLog
  const { isInContactPage, col, dialogValue, setDialogValue, value, setValue, callback, selectedOrg } = props;
  // Props for entering from AutoTracker
  const { isInAutoTrackerPage } = props;

  let currentOrg;
  if (selectedOrg) currentOrg = orgs.find((org) => org._id === selectedOrg);

  // Prop for merging contacts
  const { contacts } = props;

  const [preFillOrgUnit, setPrefillOrgUnit] = React.useState((prev) => {
    if (!isEditing) return undefined;
    if (category === 'Journalists') return orgs.find((org) => org._id === selectedContact.Org._id);
    if (category === 'Community Members') return orgs.find((org) => org._id === selectedContact.Organization._id);
    if (category === 'Experts') {
      if (!selectedContact.units) {
        return [];
      }
      return units.filter((unit) => {
        return selectedContact.units.some((id) => unit._id === id);
      });
    }
    return units.find((unit) => unit._id === selectedContact.unit);
  });

  const [openMergeContact, setOpenMergeContact] = React.useState(false);
  const [changeWarning, setChangeWarning] = React.useState(false);

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const archiveContactMutation = useArchiveContact({
    contactId: selectedContact?._id,
    category: category,
    isArchiving: true,
    setRefetchContacts: setRefreshContacts,
  });

  const duplicateCheckParamsRef = React.useRef(null);

  const duplicateCheckQuery = useQuery({
    queryKey: ['contactPersonnelDuplicates', category, selectedContact?._id],
    queryFn: async () => {
      const params = duplicateCheckParamsRef.current;
      if (!params) {
        return { data: [] };
      }
      if (category === 'Journalists') {
        return getJournalistsByCaseInsensitiveNameAndOrg(params.name, params.Org ? params.Org : '');
      }
      if (category === 'Community Members') {
        return getCommunityMembersByCaseInsensitiveNameAndOrganization(
          params.name,
          params.Organization ? params.Organization : ''
        );
      }
      return getExpertsByCaseInsensitiveNameAndUnit(params.name, params.unit ? params.unit : '');
    },
    enabled: false,
    staleTime: 0,
  });

  const createContactMutation = useMutation({
    mutationFn: (data) => {
      const payload = category === 'Experts' ? normalizeExpertPayload(data) : data;
      return API.post(url, payload);
    },
    onSuccess: async (response) => {
      if (response.status !== 201) {
        alert(response.statusText);
        return;
      }
      dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Created!' }));
      if (isInContactPage) {
        setRefreshContacts(true);
      }
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });

      if (!isInContactPage && !isInAutoTrackerPage) {
        dialogValue._id = response.data.id;
        if (category === 'Experts') {
          dialogValue.Org = response.data.Org;
          value.push({ _id: response.data.id, name: response.data.name });
          callback(value, col);
        } else if (category === 'Community Members') {
          const Organization = (currentOrg ? currentOrg : preFillOrgUnit)?._id ?? '';
          dialogValue.Organization = Organization;
          setValue({ _id: response.data.id, name: response.data.name, Organization });
          callback(dialogValue, col);
        } else {
          const Org = (currentOrg ? currentOrg : preFillOrgUnit)?._id ?? '';
          dialogValue.Org = Org;
          setValue({ _id: response.data.id, name: response.data.name, Org });
          callback(dialogValue, col);
        }
      } else if (isInAutoTrackerPage) {
        callback(dialogValue, col);
      }
    },
    onError: (error) => {
      console.log(JSON.stringify(error));
      alert(`post failed - ${error}`);
    },
  });

  const updateContactMutation = useMutation({
    mutationFn: (data) => {
      const payload = category === 'Experts' ? normalizeExpertPayload(data) : data;
      return API.put(`${url}/${selectedContact._id}`, payload);
    },
    onSuccess: async (response) => {
      if (response.status !== 200) {
        alert(response.statusText);
        return;
      }
      dispatch(openSnackbar({ severity: 'success', message: 'Contact Personnel Updated!' }));
      setRefreshContacts(true);
      await queryClient.invalidateQueries({ queryKey: ['dropdown-table'] });
      handleClose();
    },
    onError: (error) => {
      alert(`put failed - ${error}`);
    },
  });

  const concatCustomFieldsYupSchema = (baseSchema) => {
    const customFields = {};
    customFieldConfigs.forEach((config) => {
      customFields[config.fieldKey] = yup.string().nullable();
    });
    const customFieldsSchema = yup.object().shape(customFields);
    return baseSchema.concat(customFieldsSchema);
  };

  let schema, url;
  if (category === 'Journalists') {
    url = '/journalist';
    schema = yup.object().shape({
      name: yup.string().required(),
      email: yup.string().email(),
      phone: yup.string().max(32),
      cell: yup.string().max(32),
      notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
      Org: yup.string().nullable(),
    });
    if (customFieldConfigs) {
      schema = concatCustomFieldsYupSchema(schema);
    }
  } else if (category === 'Community Members') {
    url = '/community-member';
    schema = yup.object().shape({
      name: yup.string().required(),
      email: yup.string().email(),
      phone: yup.string().max(32),
      cell: yup.string().max(32),
      notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
      Organization: yup.string().nullable(),
    });
    if (customFieldConfigs) {
      schema = concatCustomFieldsYupSchema(schema);
    }
  } else {
    url = '/expert';
    schema = yup.object().shape({
      name: yup.string().required(),
      email: yup.string().email(),
      phone: yup.string().max(32),
      units: yup.array().of(
        yup.lazy((value) => {
          if (typeof value === 'string') {
            return yup.string().required();
          } else {
            return yup.object().shape({
              name: yup.string().required(),
            });
          }
        })
      ),
      language: yup.string(),
      gender: yup.string().nullable(),
      notes: yup.string().min(0).max(CONTACT_MAX_NOTES_LEN),
    });
    if (customFieldConfigs) {
      schema = concatCustomFieldsYupSchema(schema);
    }
  }

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: null,
      phone: null,
      Org: null,
      Organization: null,
      unit: null,
      units: [],
      cell: null,
      notes: null,
      language: null,
      gender: null,
    },
  });
  const classes = useStyles();

  const getTitle = () => {
    const currentCategory = category.slice(0, -1);
    if (isEditing) return `Edit ${currentCategory}`;
    return `Add New ${currentCategory}`;
  };

  const checkDuplicates = async (data) => {
    try {
      duplicateCheckParamsRef.current = data;
      const { data: response } = await duplicateCheckQuery.refetch();
      const responseData = Array.isArray(response?.data) ? response.data : [];

      if (responseData.length > 0) {
        dispatch(
          openSnackbar({
            severity: 'warning',
            message: `${
              responseData.length
            } contact personnel(s) with the same Name and Organization/Unit already exists. Please check if you are trying to add a ${category
              .toLowerCase()
              .slice(0, -1)} that already exists.`,
          })
        );
        return true;
      }
      return false;
    } catch (err) {
      alert(err);
      return false;
    }
  };

  const checkDuplicatesButExcludeSelected = async (data) => {
    try {
      duplicateCheckParamsRef.current = data;
      const { data: response } = await duplicateCheckQuery.refetch();
      const responseData = Array.isArray(response?.data) ? response.data : [];
      const filteredDuplicates = responseData.filter((oneDuplicateContact) => {
        return oneDuplicateContact._id !== selectedContact._id;
      });

      if (filteredDuplicates.length > 0) {
        dispatch(
          openSnackbar({
            severity: 'warning',
            message: `${
              filteredDuplicates.length
            } contact personnel(s) with the same Name and Organization/Unit already exists. Please check if you are trying to add a ${category
              .toLowerCase()
              .slice(0, -1)} that already exists.`,
          })
        );
        return true;
      }
      return false;
    } catch (err) {
      alert(err);
      return false;
    }
  };

  const handleArchive = async () => {
    try {
      await archiveContactMutation.mutateAsync();
      handleClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleUpdate = async (data) => {
    let normalizedData = data;
    if (category === 'Community Members') {
      normalizedData = normalizeCommunityMemberPayload(data);
    } else if (category === 'Journalists') {
      normalizedData = normalizeJournalistPayload(data);
    }
    
    if (await checkDuplicatesButExcludeSelected(normalizedData)) return;

    return updateContactMutation.mutateAsync(normalizedData);
  };

  const handleClose = () => {
    reset();
    setOpenAddContact(false);
    setChangeWarning(false);
    if (!isInContactPage) {
      if (category === 'Experts')
        setDialogValue({
          name: '',
          phone: '',
          email: '',
          unit: '',
          language: '',
          gender: '',
          _id: '',
        });
      else if (category === 'Community Members')
        setDialogValue({
          name: '',
          phone: '',
          cell: '',
          notes: '',
          email: '',
          Organization: '',
          _id: '',
        });
      else
        setDialogValue({
          name: '',
          phone: '',
          cell: '',
          notes: '',
          email: '',
          Org: '',
          _id: '',
        });
    }
  };

  const forSubmit = async (data, category) => {
    let normalizedData = data;
    if (category === 'Community Members') {
      normalizedData = normalizeCommunityMemberPayload(data);
    } else if (category === 'Journalists') {
      normalizedData = normalizeJournalistPayload(data);
    }
    if (isEditing) {
      await handleUpdate(normalizedData);
      return;
    }
    // first, check if the contact with the same name and Org already exists
    if (await checkDuplicates(normalizedData)) return;

    // if not, create a new contact
    createContactMutation.mutate(normalizedData);
    reset();
    handleClose();
  };

  useEffect(() => {
    reset({
      name: selectedContact?.name,
      email: selectedContact?.email,
      phone: selectedContact?.phone,
    });
    if (selectedContact) {
      category === 'Journalists' && setPrefillOrgUnit(orgs.find((org) => org?._id === selectedContact.Org?._id));

      if (category === 'Experts') {
        if (!selectedContact.units) {
          setPrefillOrgUnit([]);
        }
        setPrefillOrgUnit(
          units.filter((unit) => {
            if (!selectedContact.units) {
              return false;
            }
            return selectedContact.units.some((id) => unit._id === id);
          })
        );
      }

      category === 'Community Members' &&
        setPrefillOrgUnit(orgs.find((org) => org._id === selectedContact.Organization?._id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, selectedContact, category, units, reset]);

  useEffect(() => {
    if (!isInContactPage) {
      reset({
        name: dialogValue.name,
      });
    }
  }, [dialogValue?.name, isInContactPage, reset]);

  const renderCustomFields = () => {
    if (customFieldConfigs) {
      return (
        <>
          {customFieldConfigs.map((customFieldConfig) => (
            <Controller
              key={customFieldConfig.fieldKey}
              name={customFieldConfig.fieldKey}
              control={control}
              defaultValue={
                selectedContact[customFieldConfig.fieldKey] ? selectedContact[customFieldConfig.fieldKey] : ''
              }
              render={({ field }) => (
                <TextField
                  {...field}
                  label={customFieldConfig.label}
                  data-cy={`combined-personnel-modal-textfield-custom-field-${customFieldConfig.label}`}
                  variant="outlined"
                  className={classes.cellInput}
                />
              )}
            />
          ))}
        </>
      );
    }
    return null;
  };

  const renderJournalistSpecificControllers = () => {
    return (
      <>
        {isInContactPage ? (
          <Controller
            name="Org"
            control={control}
            defaultValue={currentOrg ? currentOrg._id : preFillOrgUnit?._id}
            render={({ onChange, field }) => (
              <ComboBoxCreatableOutlet
                options={orgs}
                val={currentOrg ? currentOrg._id : preFillOrgUnit?._id}
                col={'outlet'}
                textFieldVariant={'outlined'}
                disableTooltip={true}
                callback={(data) => {
                  setPrefillOrgUnit(data);
                  field.onChange(data?._id);
                  if (isInContactPage) setRefreshOrgs();
                  if (isEditing) setChangeWarning(true);
                }}
              />
            )}
          />
        ) : (
          <Controller
            name="Org"
            control={control}
            defaultValue={currentOrg ? currentOrg._id : preFillOrgUnit?._id}
            render={({ onChange, field }) => (
              <Autocomplete
                {...field}
                disablePortal
                clearOnEscape
                openOnFocus
                autoHighlight
                options={orgs}
                getOptionLabel={(option) => option.name}
                value={currentOrg ? currentOrg : preFillOrgUnit}
                onChange={(_, data) => {
                  setPrefillOrgUnit(data);
                  field.onChange(data ? data._id : data);
                  if (isEditing) setChangeWarning(true);
                }}
                data-cy="org-autoComplete-CombinedContactPersonnelModal"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    variant="outlined"
                    className={classes.cellInput}
                    error={errors.Org}
                    helperText={errors.Org && errors.Org.message}
                    data-cy="org-input-CombinedContactPersonnelModal"
                  />
                )}
              />
            )}
          />
        )}

        <Controller
          name="cell"
          control={control}
          defaultValue={isEditing ? selectedContact.cell : ''}
          render={({ field }) => (
            <TextField
              {...field}
              label="Cell"
              variant="outlined"
              className={classes.cellInput}
              error={errors.phone}
              helperText={errors.phone && errors.phone.message}
              data-cy="cell-input-CombinedContactPersonnelModal"
            />
          )}
        />

        {renderCustomFields()}
      </>
    );
  };

  return (
    <Dialog
      open={openAddContact}
      onClose={handleClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <form
        onSubmit={(event) => {
          if (event) {
            if (typeof event.preventDefault === 'function') {
              event.preventDefault();
            }
            if (typeof event.stopPropagation === 'function') {
              event.stopPropagation();
            }
          }
          return handleSubmit((data) => forSubmit(data, category))(event);
        }}
        data-cy="form-CombinedContactPersonnelModal"
      >
        <DialogTitle>{getTitle()}</DialogTitle>
        <DialogContent className={classes.dialog}>
          <Stack
            spacing={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '1.5rem',
            }}
          >
            <Controller
              name="name"
              control={control}
              defaultValue={() => {
                if (isEditing) return selectedContact.name;
                if (!isInContactPage) return dialogValue.name;
                return '';
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  variant="outlined"
                  className={classes.cellInput}
                  error={!!errors.name}
                  helperText={errors.name ? errors.name.message : ''}
                  data-cy="name-input-CombinedContactPersonnelModal"
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              defaultValue={isEditing ? selectedContact.email : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  variant="outlined"
                  className={classes.cellInput}
                  error={errors.email}
                  helperText={errors.email && errors.email.message}
                  data-cy="email-input-CombinedContactPersonnelModal"
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              defaultValue={isEditing ? selectedContact.phone : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone"
                  variant="outlined"
                  className={classes.cellInput}
                  error={errors.phone}
                  helperText={errors.phone && errors.phone.message}
                  data-cy="phone-input-CombinedContactPersonnelModal"
                />
              )}
            />

            {category === 'Journalists' ? (
              renderJournalistSpecificControllers()
            ) : category === 'Experts' ? (
              <>
                <Controller
                  name="units"
                  control={control}
                  defaultValue={isEditing ? preFillOrgUnit : []}
                  render={({ onChange, field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      disablePortal
                      clearOnEscape
                      openOnFocus
                      autoHighlight
                      options={units}
                      getOptionLabel={(option) => option.name}
                      value={preFillOrgUnit}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          return (
                            <Chip
                              sx={{ 'MuiChip-label': { fontSize: '12px' } }}
                              size="small"
                              variant="outlined"
                              label={option.name}
                              {...getTagProps({ index })}
                            />
                          );
                        })
                      }
                      onChange={(_, data) => {
                        setPrefillOrgUnit(data);
                        field.onChange(data ? data.map((d) => d._id) : []);
                        if (isEditing) setChangeWarning(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Units"
                          variant="outlined"
                          className={classes.cellInput}
                          error={errors.unit}
                          helperText={errors.unit && errors.unit.message}
                          data-cy="unit-input-CombinedContactPersonnelModal"
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="language"
                  control={control}
                  defaultValue={isEditing ? selectedContact.language : ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Language"
                      variant="outlined"
                      className={classes.cellInput}
                      error={errors.language}
                      helperText={errors.language && errors.language.message}
                      data-cy="language-input-CombinedContactPersonnelModal"
                    />
                  )}
                />
                <Controller
                  name="gender"
                  control={control}
                  defaultValue={isEditing ? selectedContact?.gender : ''}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      clearOnEscape
                      openOnFocus
                      autoHighlight
                      options={genderOptions}
                      getOptionLabel={(option) => option.name ?? option}
                      isOptionEqualToValue={(option, value) => option.name === value}
                      onChange={(_, data) => {
                        field.onChange(data ? data.name : data);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Gender"
                          variant="outlined"
                          className={classes.cellInput}
                          error={errors.gender}
                          helperText={errors.gender && errors.gender.message}
                          data-cy="gender-input-CombinedContactPersonnelModal"
                        />
                      )}
                    />
                  )}
                />
                {renderCustomFields()}
              </>
            ) : (
              <>
                <Controller
                  name="Organization"
                  control={control}
                  defaultValue={currentOrg ? currentOrg._id : preFillOrgUnit?._id}
                  render={({ onChange, field }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      clearOnEscape
                      openOnFocus
                      autoHighlight
                      options={orgs}
                      getOptionLabel={(option) => option.name}
                      value={currentOrg ? currentOrg : preFillOrgUnit}
                      onChange={(_, data) => {
                        setPrefillOrgUnit(data);
                        field.onChange(data ? data._id : data);
                        if (isEditing) setChangeWarning(true);
                      }}
                      data-cy="organization-autoComplete-CombinedContactPersonnelModal"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Organization"
                          variant="outlined"
                          className={classes.cellInput}
                          error={errors.Organization}
                          helperText={errors.Organization && errors.Organization.message}
                          data-cy="organization-input-CombinedContactPersonnelModal"
                        />
                      )}
                    />
                  )}
                />
                <Controller
                  name="cell"
                  control={control}
                  defaultValue={isEditing ? selectedContact.cell : ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Cell"
                      variant="outlined"
                      className={classes.cellInput}
                      error={errors.phone}
                      helperText={errors.phone && errors.phone.message}
                      data-cy="cell-input-CombinedContactPersonnelModal"
                    />
                  )}
                />
                {renderCustomFields()}
              </>
            )}

            <br />
            <Controller
              name="notes"
              control={control}
              defaultValue={isEditing ? selectedContact.notes : ''}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  multiline
                  minRows={2}
                  maxRows={5}
                  variant="outlined"
                  inputProps={{ maxLength: CONTACT_MAX_NOTES_LEN }}
                  helperText={
                    <Box className={classes.charCountText}>{`${
                      watch('notes').length
                    }/${CONTACT_MAX_NOTES_LEN} character(s)`}</Box>
                  }
                  className={classes.cellInput}
                  error={errors.notes}
                  data-cy="notes-input-CombinedContactPersonnelModal"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-cy="cancel-button-CombinedContactPersonnelModal">
            Cancel
          </Button>
          {isEditing && (
            <Button
              onClick={() => setOpenMergeContact(true)}
              startIcon={<MergeIcon />}
              data-cy="merge-button-CombinedContactPersonnelModal"
            >
              Merge
            </Button>
          )}
          {isEditing && (
            <Button
              onClick={() => handleArchive()}
              startIcon={<ArchiveIcon />}
              data-cy="archive-button-CombinedContactPersonnelModal"
            >
              Archive
            </Button>
          )}
          {isEditing && (
            <Button type="submit" startIcon={<SyncAltIcon />} data-cy="update-button-CombinedContactPersonnelModal">
              Update
            </Button>
          )}
          {!isEditing && (
            <Button type="submit" startIcon={<AddCircleIcon />} data-cy="add-button-CombinedContactPersonnelModal">
              Add
            </Button>
          )}
        </DialogActions>
      </form>

      <MergeContactPersonnelModal
        openMergeContact={openMergeContact}
        setOpenMergeContact={setOpenMergeContact}
        selectedContact={selectedContact}
        category={category}
        contacts={contacts}
        orgs={orgs}
        units={units}
        setRefreshContacts={setRefreshContacts}
        setOpenAddContact={setOpenAddContact}
      />
      <AlertChangeOrgUnit open={changeWarning} setOpen={setChangeWarning} category={category} />
    </Dialog>
  );
};

export default CombinedContactPersonnelModal;
